import { useState } from 'react'
import type { ProjectContextResponse, UpdateProjectAiVersionRequest } from '@ai-meeting/shared'
import { PencilIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { formatDate, formatDateTime } from '@/utils/formatDateTime'
import type { ProjectContextVersion } from '../projectContextUi'

/** The AI version body. */
const CONTEXT_BOX_CLASS =
  'rounded-xl border border-[var(--border-color)] bg-slate-50/30 p-[17px] text-xs leading-[19.5px] text-slate-700 dark:bg-[var(--bg-primary)]/60 dark:text-slate-300'

type Props = {
  context: ProjectContextResponse
  saving: boolean
  errorMessage?: string | null
  onSave: (body: UpdateProjectAiVersionRequest) => Promise<unknown>
  /** The version the next meeting will use, named beside the label. */
  latestVersion?: ProjectContextVersion | null
}

/**
 * "Project context" card: the AI version, corrected in place.
 *
 * The description the user used to write is gone. The editable text is
 * `learnedText` — the same value the next meeting receives — and saving files
 * a `human` version upstream. Version badges name the active one.
 */
export default function ProjectContextCard({
  context,
  saving,
  errorMessage,
  onSave,
  latestVersion = null,
}: Props) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)
  const [editing, setEditing] = useState(false)
  const [draftText, setDraftText] = useState('')

  function startEdit() {
    setDraftText(context.learnedText ?? '')
    setEditing(true)
  }

  async function handleSave() {
    try {
      await onSave({ text: draftText.trim() ? draftText.trim() : null })
      setEditing(false)
    } catch {
      // `useProjectContext` already put the server's own sentence on
      // `errorMessage`, which this editor renders. Letting it travel any further
      // would only produce an unhandled rejection and close nothing.
    }
  }

  // Meeting versions read "v5 from Meeting #22"; human corrections have no
  // meeting to point at, so the source stands on its own beside the label.
  const versionSource = latestVersion
    ? latestVersion.sourceIsMeeting
      ? `${latestVersion.label} ${t.projectDetailContextLatestFrom} ${latestVersion.sourceLabel}`
      : `${latestVersion.label} · ${latestVersion.sourceLabel}`
    : null
  const hint = latestVersion
    ? `${t.projectDetailContextLatestHint} - ${versionSource} · ${formatDateTime(latestVersion.createdAt, uiLocale)}`
    : context.learnedAt
      ? `${t.projectDetailContextInUse} · ${t.projectDetailContextUpdatedAt} ${formatDate(context.learnedAt, uiLocale)}`
      : t.projectDetailContextHint

  return (
    <section
      data-testid="project-context-card"
      className="flex flex-col gap-2.5 rounded-2xl border border-[var(--border-color)]/80 bg-[var(--bg-secondary)] p-[25px] shadow-[var(--shadow-sm)]"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-semibold tracking-[-0.4px] text-[var(--text-primary)]">
          {t.projectDetailContextTitle}
        </h2>
        <p className="text-xs leading-4 text-[var(--text-secondary)]">{hint}</p>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.55px] text-slate-700 dark:text-slate-300">
            {t.projectDetailContextAiVersionLabel}
          </span>
          {latestVersion && (
            <span className="shrink-0 rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-medium leading-5 text-indigo-600 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300">
              {t.projectDetailContextLatestBadge} - {latestVersion.label}
            </span>
          )}
        </div>
        {!editing && (
          <button
            type="button"
            aria-label={t.projectDetailContextEditAiVersion}
            onClick={startEdit}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]"
          >
            <PencilIcon className="h-3.5 w-3.5" />
            {t.edit}
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-3">
          <textarea
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            rows={6}
            className="input-field max-h-[500px] min-h-[300px] w-full resize-y overflow-y-auto rounded-xl px-[17px] py-[17px] text-xs leading-[19.5px]"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="btn-primary rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
            >
              {t.projectDetailContextSave}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setEditing(false)}
              className="rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)]"
            >
              {t.projectDetailContextCancel}
            </button>
          </div>
          {errorMessage && (
            <p role="alert" className="text-xs text-[var(--error)]">
              {errorMessage}
            </p>
          )}
        </div>
      ) : (
        <div className={CONTEXT_BOX_CLASS}>
          {context.learnedText ? (
            <p className="whitespace-pre-wrap">{context.learnedText}</p>
          ) : (
            <p className="text-[var(--text-tertiary)]">{t.projectDetailContextEmpty}</p>
          )}
        </div>
      )}
    </section>
  )
}
